import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiClock, FiDollarSign, FiAward, FiGlobe } from 'react-icons/fi';
import { Card, Button, Badge, Avatar, StarRating, DayLabel, Spinner } from '../components/Components';
import { coachAPI } from '../utils/api';
import './CoachProfile.css';

export default function CoachProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [coachRes, availRes] = await Promise.all([
          coachAPI.getById(id),
          coachAPI.getAvailability(id),
        ]);
        setCoach(coachRes.data.coach);
        setAvailability(availRes.data.availability);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  if (loading) return <div className="page-container"><Spinner /></div>;
  if (!coach) return <div className="page-container"><p>Coach not found</p></div>;

  const initials = coach.avatar || coach.name?.split(' ').map(n => n[0]).join('');

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      <Link to="/search" className="back-link"><FiArrowLeft size={14} /> Back to Search</Link>

      {/* Profile Header */}
      <Card className="cp-header">
        <div className="cp-header__main">
          <Avatar initials={initials} size="xl" />
          <div className="cp-header__info">
            <h2 className="cp-header__name">{coach.name}</h2>
            <StarRating rating={coach.rating} count={coach.totalRatings} />
            <div className="cp-header__meta">
              {coach.location && <span className="cp-header__meta-item"><FiMapPin size={13} /> {coach.location}</span>}
              <span className="cp-header__meta-item"><FiClock size={13} /> {coach.totalSessions} sessions</span>
              <span className="cp-header__meta-item"><FiDollarSign size={13} /> ${coach.rate}/hr</span>
              {coach.sessionDuration && <span className="cp-header__meta-item"><FiClock size={13} /> {coach.sessionDuration} min sessions</span>}
            </div>
          </div>
          <Button onClick={() => navigate(`/book/${coach._id}`)}>Book a Session</Button>
        </div>
      </Card>

      {/* Info Grid */}
      <div className="cp-grid">
        <Card>
          <h3 className="cp-section-title">About</h3>
          <p className="cp-text">{coach.bio}</p>
        </Card>
        <Card>
          <h3 className="cp-section-title">Coaching Interests</h3>
          <p className="cp-text cp-text--italic">"{coach.coachingInterests}"</p>
        </Card>
      </div>

      {/* Details */}
      <Card className="cp-details">
        <div className="cp-details__grid">
          <div>
            <h3 className="cp-section-title">Expertise</h3>
            <div className="cp-badges">{coach.expertise?.map(e => <Badge key={e} variant="primary">{e}</Badge>)}</div>
          </div>
          {coach.certifications?.length > 0 && (
            <div>
              <h3 className="cp-section-title"><FiAward size={14} /> Certifications</h3>
              <div className="cp-badges">{coach.certifications.map(c => <Badge key={c} variant="success">{c}</Badge>)}</div>
            </div>
          )}
          {coach.languages?.length > 0 && (
            <div>
              <h3 className="cp-section-title"><FiGlobe size={14} /> Languages</h3>
              <div className="cp-badges">{coach.languages.map(l => <Badge key={l}>{l}</Badge>)}</div>
            </div>
          )}
          {coach.experience && (
            <div>
              <h3 className="cp-section-title">Experience</h3>
              <p className="cp-text">{coach.experience}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Available Slots - Day Wise, Today First */}
      <Card className="cp-availability">
        <div className="cp-availability__header">
          <h3 className="cp-section-title">Available Slots (Next 2 Weeks)</h3>
          <Button size="sm" onClick={() => navigate(`/book/${coach._id}`)}>Book Now</Button>
        </div>

        {availability.length === 0 ? (
          <div className="cp-empty">No available slots at the moment. Check back later!</div>
        ) : (
          <div className="cp-slots-list">
            {availability.map(({ date, label, slots }) => (
              <div key={date} className="cp-slot-day">
                <DayLabel label={label} date={new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} />
                <div className="cp-slot-times">
                  {slots.map(time => (
                    <button
                      key={time}
                      className="cp-slot-time"
                      onClick={() => navigate(`/book/${coach._id}?date=${date}&time=${encodeURIComponent(time)}`)}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Reviews */}
      {coach.reviews?.length > 0 && (
        <Card>
          <h3 className="cp-section-title">Reviews</h3>
          <div className="cp-reviews">
            {coach.reviews.map((r, i) => (
              <div key={i} className="cp-review">
                <div className="cp-review__header">
                  <strong>{r.candidateName}</strong>
                  <StarRating rating={r.rating} />
                </div>
                {r.comment && <p className="cp-review__text">{r.comment}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
